import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ShieldCheck,
  LineChart,
  Sparkles,
  Smartphone,
  Rocket,
  Gift,
  CheckCircle2,
} from "lucide-react";
import Seo from "@/components/seo/Seo";
import { MOBILE_APP_API } from "@/lib/site";

/**
 * Local fallback tier metadata — used when the mobile-app backend can't
 * be reached (dev, first paint before fetch, network hiccup). Real
 * pricing / totals always come from the backend on live use.
 */
const FALLBACK_PACKAGES = [
  {
    id: "starter",
    label: "Starter",
    description: "3 months of Safe to Spend 365 Premium per employee.",
    months: 3,
    per_employee_display: "$7.99",
    total_display: null,
    features: [
      "3 months Premium access per employee",
      "Master code + individual codes",
      "Basic redemption analytics",
    ],
    is_custom: false,
  },
  {
    id: "standard",
    label: "Standard",
    description: "6 months of Safe to Spend 365 Premium per employee.",
    months: 6,
    per_employee_display: "$12.99",
    total_display: null,
    features: [
      "6 months Premium access per employee",
      "Aggregate wellness dashboard",
      "Priority email support",
    ],
    is_custom: false,
    highlighted: true,
  },
  {
    id: "premium",
    label: "Premium",
    description: "12 months of Safe to Spend 365 Premium per employee.",
    months: 12,
    per_employee_display: "$19.99",
    total_display: null,
    features: [
      "12 months Premium access per employee",
      "Volume discounts kick in from 50 employees",
      "SSO / SCIM available",
    ],
    is_custom: false,
  },
  {
    id: "enterprise",
    label: "Enterprise",
    description: "Custom terms, dedicated onboarding, SLA.",
    months: null,
    per_employee_display: "Custom quote",
    total_display: null,
    features: [
      "1,000+ employees",
      "Dedicated success manager",
      "Custom SLA + procurement paperwork",
    ],
    is_custom: true,
  },
];

export default function ForEmployers() {
  const [employees, setEmployees] = useState(100);
  const [packages, setPackages] = useState(FALLBACK_PACKAGES);
  const [volumeTiers, setVolumeTiers] = useState([]);
  const [loading, setLoading] = useState(false);

  // Debounced fetch of live pricing every time the slider settles.
  useEffect(() => {
    if (!MOBILE_APP_API) return;
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(
          `${MOBILE_APP_API}/api/corporate/packages`,
          { params: { employee_count: employees }, timeout: 5000 },
        );
        if (cancelled) return;
        if (Array.isArray(data?.packages) && data.packages.length) {
          setPackages(data.packages);
        }
        if (Array.isArray(data?.volume_tiers)) {
          setVolumeTiers(data.volume_tiers);
        }
      } catch {
        // Silent — fallback data is already rendered.
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [employees]);

  const activeTier = useMemo(() => {
    if (!volumeTiers.length) return null;
    // Highest threshold ≤ current count.
    const matches = volumeTiers.filter((t) => employees >= t.threshold);
    return matches[matches.length - 1] || volumeTiers[0];
  }, [volumeTiers, employees]);

  return (
    <>
      <Seo
        title="Employee Financial Wellness Benefit | Safe to Spend 365 for Business"
        description="Give your team a sponsored Premium membership. Aggregate wellness analytics for HR, private financial data for employees. From $7.99/employee."
        path="/for-employers"
        image="https://atlasforgefinancial.com/og-for-employers.png"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Product",
            "name": "Safe to Spend 365 for Business",
            "description":
              "Employee financial wellness benefit with aggregate HR analytics",
            "brand": { "@type": "Brand", "name": "Safe to Spend 365" },
            "offers": {
              "@type": "AggregateOffer",
              "lowPrice": "7.99",
              "highPrice": "19.99",
              "priceCurrency": "USD",
              "offerCount": 4,
            },
          },
        ]}
      />

      {/* ── HERO ─────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 af-soft-glow pointer-events-none" />
        <div className="relative mx-auto max-w-6xl px-6 lg:px-10 pt-20 pb-16 md:pt-28">
          <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500">
            For employers
          </p>
          <h1
            data-testid="for-employers-hero-title"
            className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#0A1128]"
          >
            Financial wellness for your team.
          </h1>
          <p className="mt-5 max-w-2xl text-[16px] text-slate-600 leading-relaxed">
            Give employees clarity on what they can safely spend each day.
            Aggregate wellness analytics for HR — zero individual data
            exposure. Deploy in under five minutes with one master code.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link
              to="/for-employers/inquiry"
              data-testid="for-employers-cta-primary"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#0A1128] text-white px-5 py-2.5 text-[14px] font-medium hover:bg-[#1E293B] transition-colors"
            >
              Request a quote <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href="#pricing"
              data-testid="for-employers-cta-secondary"
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-5 py-2.5 text-[14px] font-medium text-[#0A1128] hover:border-slate-400 transition-colors"
            >
              See pricing
            </a>
          </div>
          <div className="mt-10 flex flex-wrap gap-x-6 gap-y-2 text-[13px] text-slate-500 max-w-3xl">
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5" />
              Aggregate-only analytics
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5" />
              One master code
            </span>
            <span className="inline-flex items-center gap-1.5">
              <LineChart className="h-3.5 w-3.5" />
              Stripe invoice
            </span>
          </div>
        </div>
      </section>

      {/* ── FEATURES ─────────────────────────────────────────────────── */}
      <section className="relative border-t border-slate-100 bg-[#F7F9FC]">
        <div className="mx-auto max-w-6xl px-6 lg:px-10 py-16 md:py-20">
          <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500">
            Why teams choose Safe to Spend 365
          </p>
          <h2 className="mt-2 font-display text-3xl md:text-4xl text-[#0A1128] max-w-2xl leading-tight">
            Everything a benefit leader needs to ship this in week one.
          </h2>
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <FeatureCard
              icon={<Gift className="h-5 w-5" />}
              title="Sponsored Premium membership."
              body="Give employees 3, 6, or 12 months of Safe to Spend 365 Premium — no payroll deduction, no per-seat licensing headaches."
            />
            <FeatureCard
              icon={<ShieldCheck className="h-5 w-5" />}
              title="Privacy-first by design."
              body="You never see individual employee data. Ever. HR sees aggregate wellness numbers — nothing personally identifiable."
            />
            <FeatureCard
              icon={<LineChart className="h-5 w-5" />}
              title="Aggregate wellness dashboard."
              body="Adoption, overdraft avoidance, savings velocity, dollars kept in employees' pockets — all anonymised and rolled up."
            />
            <FeatureCard
              icon={<Smartphone className="h-5 w-5" />}
              title="iOS and Android."
              body="Employees redeem in 30 seconds on their phone. Master code + individual codes flow ready out of the box."
            />
            <FeatureCard
              icon={<Rocket className="h-5 w-5" />}
              title="Setup in under 5 minutes."
              body="Approve the invoice, get your master code, share it with your team. That's it. No IT project, no rollout plan."
            />
            <FeatureCard
              icon={<Sparkles className="h-5 w-5" />}
              title="Employees actually feel it."
              body="No abstract education modules. Employees open the app on payday and see one clear number — what's safe to spend today."
            />
          </div>
        </div>
      </section>

      {/* ── PRICING (live) ───────────────────────────────────────────── */}
      <section id="pricing" className="relative">
        <div className="mx-auto max-w-6xl px-6 lg:px-10 py-16 md:py-20">
          <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500">
            Per-employee pricing
          </p>
          <h2 className="mt-2 font-display text-3xl md:text-4xl text-[#0A1128] max-w-2xl leading-tight">
            Pay per employee. Volume discounts kick in automatically.
          </h2>

          <div className="mt-10 grid lg:grid-cols-[300px_1fr] gap-8 items-start">
            <div className="rounded-2xl border border-slate-200 bg-white p-6">
              <label
                htmlFor="employee_count"
                className="text-[12px] uppercase tracking-wider text-slate-500 font-semibold"
              >
                Number of employees
              </label>
              <p
                data-testid="employee-count-display"
                className="mt-2 font-display text-4xl text-[#0A1128]"
              >
                {employees.toLocaleString()}
              </p>
              <input
                id="employee_count"
                type="range"
                min={1}
                max={5000}
                step={employees >= 1000 ? 100 : employees >= 250 ? 25 : employees >= 50 ? 10 : 1}
                value={employees}
                onChange={(e) => setEmployees(Number(e.target.value))}
                data-testid="employee-count-slider"
                className="w-full mt-3 accent-[#0A1128]"
              />
              <div className="flex justify-between text-[10.5px] uppercase tracking-wider text-slate-400 mt-1">
                <span>1</span>
                <span>5,000+</span>
              </div>
              <p
                data-testid="pricing-loading-hint"
                className="mt-5 text-[12px] text-slate-500 leading-relaxed"
              >
                {loading
                  ? "Fetching live prices…"
                  : "Prices update live from our billing system."}
              </p>
              {activeTier && (
                <div
                  data-testid="active-volume-tier"
                  className="mt-4 rounded-xl bg-slate-50 border border-slate-200 p-3"
                >
                  <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                    Current tier
                  </p>
                  <p className="mt-1 text-[13px] text-[#0A1128] font-medium">
                    {activeTier.label}
                    {activeTier.premium_pct_off
                      ? ` · ${activeTier.premium_pct_off}% off Premium`
                      : ""}
                  </p>
                  {activeTier.note && (
                    <p className="mt-1 text-[12px] text-slate-500">
                      {activeTier.note}
                    </p>
                  )}
                </div>
              )}
              {employees >= 1000 && (
                <div className="mt-4 rounded-xl bg-amber-50 border border-amber-200 p-3">
                  <p className="text-[12.5px] text-amber-900 leading-snug">
                    <strong>1,000+ employees?</strong> Enterprise pricing
                    includes SSO, SCIM, dedicated onboarding, and a custom SLA.
                  </p>
                </div>
              )}
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              {packages.map((pkg) => (
                <PricingCard
                  key={pkg.id}
                  pkg={pkg}
                  employees={employees}
                  highlighted={pkg.id === "standard"}
                />
              ))}
            </div>
          </div>

          <div className="mt-10 flex justify-center">
            <Link
              to="/for-employers/inquiry"
              data-testid="pricing-cta"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#0A1128] text-white px-6 py-3 text-[14px] font-medium hover:bg-[#1E293B] transition-colors"
            >
              Request a quote for {employees.toLocaleString()} employees
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ────────────────────────────────────────────────── */}
      <section className="relative border-t border-slate-100">
        <div className="mx-auto max-w-4xl px-6 lg:px-10 py-16 md:py-20 text-center">
          <h2 className="font-display text-3xl md:text-4xl text-[#0A1128]">
            Ready to give your team clarity on their money?
          </h2>
          <p className="mt-3 text-[15px] text-slate-600 max-w-2xl mx-auto">
            One master code, one Stripe invoice, one dashboard. We'll reach out
            within one business day.
          </p>
          <div className="mt-8">
            <Link
              to="/for-employers/inquiry"
              data-testid="for-employers-final-cta"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#0A1128] text-white px-6 py-3 text-[14px] font-medium hover:bg-[#1E293B] transition-colors"
            >
              Request a quote <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}

function FeatureCard({ icon, title, body }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 af-shadow-card">
      <div className="inline-flex items-center justify-center h-9 w-9 rounded-xl bg-[#0A1128] text-white">
        {icon}
      </div>
      <h3 className="mt-4 font-display text-[18px] text-[#0A1128]">{title}</h3>
      <p className="mt-2 text-[14px] text-slate-600 leading-relaxed">{body}</p>
    </div>
  );
}

function PricingCard({ pkg, employees, highlighted }) {
  return (
    <div
      data-testid={`pricing-card-${pkg.id}`}
      className={`relative rounded-2xl border p-5 bg-white ${
        highlighted
          ? "border-[#0A1128] shadow-lg ring-2 ring-[#0A1128]/10"
          : "border-slate-200"
      }`}
    >
      {highlighted && (
        <span className="absolute -top-2.5 left-5 rounded-full bg-[#FF5A36] text-white text-[10.5px] font-semibold uppercase tracking-wider px-2 py-0.5">
          Most popular
        </span>
      )}
      <h3 className="font-display text-[20px] text-[#0A1128]">{pkg.label}</h3>
      <p className="mt-1 text-[13px] text-slate-500 leading-snug">
        {pkg.description}
      </p>
      <div className="mt-5">
        {pkg.is_custom ? (
          <>
            <p className="font-display text-3xl text-[#0A1128]">
              {pkg.per_employee_display || "Custom"}
            </p>
            <p className="mt-1 text-[12px] text-slate-500">Talk to sales</p>
          </>
        ) : (
          <>
            <p className="font-display text-3xl text-[#0A1128]">
              {pkg.per_employee_display}
              <span className="text-[13px] text-slate-500 font-sans font-normal">
                {" "}
                / employee
              </span>
            </p>
            {pkg.total_display && (
              <p className="mt-1 text-[12.5px] text-slate-600">
                ≈ <strong>{pkg.total_display}</strong> total for{" "}
                {employees.toLocaleString()}
              </p>
            )}
          </>
        )}
      </div>
      {Array.isArray(pkg.features) && pkg.features.length > 0 && (
        <ul className="mt-5 space-y-2">
          {pkg.features.map((f) => (
            <li
              key={f}
              className="flex items-start gap-2 text-[13px] text-slate-600 leading-snug"
            >
              <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 text-[#0A1128] shrink-0" />
              <span>{f}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
